/**
 * PaymentsSuccess.jsx — landing page after Stripe / Paystack redirects back.
 *
 * Stripe → /payments/success?session_id=cs_xxx (polled via checkout-status)
 * Paystack → /payments/success?reference=ref_xxx (polled via by-reference)
 *
 * The page polls the backend for up to ~30s to confirm the payment, then
 * either celebrates or surfaces an error. Coins / book unlocks happen
 * server-side as soon as the polling endpoint flips the transaction to
 * `paid` (idempotent — webhook + this poll race safely).
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Coins, BookOpen, Lightbulb } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_POLLS = 12;       // 12 × 2.5s = 30s
const POLL_INTERVAL_MS = 2500;

const PaymentsSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const reference = params.get('reference');
  const paypalOrderId = params.get('paypal_order_id') || params.get('token'); // PayPal sends ?token=ORDER_ID
  const [status, setStatus] = useState('polling'); // polling | paid | failed | timeout
  const [tx, setTx] = useState(null);
  const [error, setError] = useState('');
  const attemptsRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    if (!sessionId && !reference && !paypalOrderId) {
      setStatus('failed');
      setError('Missing payment reference.');
      return undefined;
    }

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        let url;
        if (sessionId) {
          url = `${BACKEND_URL}/api/payments/checkout-status/${encodeURIComponent(sessionId)}`;
        } else if (paypalOrderId) {
          url = `${BACKEND_URL}/api/payments/paypal-status/${encodeURIComponent(paypalOrderId)}`;
        } else {
          url = `${BACKEND_URL}/api/payments/by-reference/${encodeURIComponent(reference)}`;
        }
        const res = await authFetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setTx(data);
        const ps = data.payment_status;
        if (ps === 'paid') {
          setStatus('paid');
          return;
        }
        if (ps === 'failed' || ps === 'expired' || data.status === 'expired') {
          setStatus('failed');
          setError(`Payment ${ps || data.status}.`);
          return;
        }
        if (attemptsRef.current >= MAX_POLLS) {
          setStatus('timeout');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        if (attemptsRef.current >= MAX_POLLS) {
          setStatus('timeout');
        } else {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, reference, paypalOrderId]);

  const goNext = () => {
    if (tx?.kind === 'book' && tx?.content_id) {
      navigate(`/reader/${tx.content_id}`);
    } else if (tx?.kind === 'subscription') {
      navigate('/dashboard');
    } else if (tx?.kind === 'vault_access') {
      navigate('/vault');
    } else {
      navigate('/shop');
    }
  };

  return (
    <main className="min-h-[80vh] flex items-center justify-center px-4">
      <div
        className="max-w-md w-full rounded-2xl border bg-slate-900/80 backdrop-blur-xl border-slate-700 p-8 text-center shadow-2xl"
        data-testid="payments-success-card"
      >
        {status === 'polling' && (
          <>
            <Loader2 className="w-10 h-10 text-amber-300 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Confirming your payment…</h1>
            <p className="text-sm text-slate-400">This usually takes a few seconds.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" data-testid="payments-success-paid" />
            <h1 className="text-2xl font-bold text-white mb-2">
              {tx?.kind === 'book'
                ? 'Unlocked!'
                : tx?.kind === 'subscription'
                  ? `Welcome to ${tx?.plan_tier === 'premium_top' ? 'Top Tier' : tx?.plan_tier === 'writer_ai_tools' ? 'Scribe AI Tools' : 'Premium'}!`
                  : tx?.kind === 'vault_access'
                    ? 'Vault unlocked'
                    : 'Coins added'}
            </h1>
            {tx?.kind === 'coin_pack' && (
              <p className="text-sm text-slate-300 mb-6 inline-flex items-center gap-1">
                <Coins className="w-4 h-4 text-amber-300" />
                <span className="text-amber-200 font-bold tabular-nums">{tx?.coins}</span>
                <span>coins are now in your wallet.</span>
              </p>
            )}
            {tx?.kind === 'book' && (
              <p className="text-sm text-slate-300 mb-6 inline-flex items-center gap-1">
                <BookOpen className="w-4 h-4 text-indigo-300" />
                <span>Your purchase is unlocked. Enjoy!</span>
              </p>
            )}
            {tx?.kind === 'subscription' && (
              <p className="text-sm text-slate-300 mb-6">
                Your <span className="text-amber-200 font-semibold">{tx?.plan_months}-month</span>{' '}
                pass is active. Cancel anytime from your profile.
              </p>
            )}
            {tx?.kind === 'vault_access' && (
              <p className="text-sm text-slate-300 mb-6">
                Payment received. An admin will message you with your private vault code shortly.
              </p>
            )}
            {/* Card-issuer hint — surfaces when Stripe reported the card's
                issuing country differs from the user's saved region. Lets
                the user one-tap save fees next time. */}
            {tx?.gateway_hint && (
              <div
                className="mb-6 mx-auto max-w-sm rounded-xl border border-emerald-400/40 bg-emerald-500/10 p-3 text-left"
                data-testid="payments-success-gateway-hint"
              >
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-emerald-100 leading-relaxed">
                    <span className="font-bold">Lower-fee tip:</span> Your card is from{' '}
                    <span className="font-bold text-emerald-300">
                      {tx.gateway_hint.card_country}
                    </span>
                    . Next time pay with{' '}
                    <span className="font-bold text-white capitalize">
                      {tx.gateway_hint.recommended_provider}
                    </span>
                    {' '}({tx.gateway_hint.recommended_currency}) to save on FX + processor fees.
                  </div>
                </div>
              </div>
            )}
            <Button
              onClick={goNext}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full px-8"
              data-testid="payments-success-cta"
            >
              {tx?.kind === 'book'
                ? 'Open'
                : tx?.kind === 'subscription'
                  ? 'Go to Dashboard'
                  : tx?.kind === 'vault_access'
                    ? 'Go to Vault'
                    : 'Back to Shop'}
            </Button>
          </>
        )}
        {(status === 'failed' || status === 'timeout') && (
          <>
            <XCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" data-testid="payments-success-failed" />
            <h1 className="text-xl font-bold text-white mb-2">
              {status === 'timeout' ? 'Still processing' : 'Payment didn\'t complete'}
            </h1>
            <p className="text-sm text-slate-400 mb-6">
              {status === 'timeout'
                ? 'We haven\'t had confirmation yet. Refresh in a minute or check your wallet — you\'ll be credited as soon as we hear back.'
                : (error || 'Please try again or use a different card.')}
            </p>
            <Button onClick={() => navigate('/shop')} variant="outline" className="rounded-full">
              Back to Shop
            </Button>
          </>
        )}
      </div>
    </main>
  );
};

export default PaymentsSuccess;
